import {alpha, SxProps, Theme} from '@mui/material';
import theme from 'src/theme';
import {StatsStripTone} from './StatsStrip.types';

/**
 * The block every list page opens with.
 * <p>
 * It wears the identity card's anatomy — icon badge, uppercase label, one large tabular number —
 * because on the pages that have both it sits directly under that card, and on the pages that do not
 * it is what the card would have been.
 */
export const cardSx: SxProps<Theme> = {
    background: theme.palette.common.white,
    borderRadius: '14px',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04)',
    overflow: 'hidden',
};

export const headerSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 2.25,
    flexWrap: 'wrap',
    px: 3,
    py: 2.5,
    background: `linear-gradient(180deg, ${theme.palette.primaryColor.A50} 0%, ${theme.palette.common.white} 100%)`,
};

export const titleSx: SxProps<Theme> = {
    fontSize: '1.0625rem',
    fontWeight: 600,
    letterSpacing: '-0.01em',
};

export const subtitleSx: SxProps<Theme> = {
    fontSize: '0.75rem',
    color: theme.palette.neutral[500],
    mt: 0.2,
};

interface Tone {
    background: string;
    border: string;
    color: string;
}

/** Named by meaning, resolved from the theme — no colour in this file is invented. */
export const tones: Record<StatsStripTone, Tone> = {
    primary: {
        background: theme.palette.primaryColor.A50 as string,
        border: theme.palette.primaryColor.A100 as string,
        color: theme.palette.primary.main,
    },
    /**
     * Blue with something behind it, where `primary` is a tint.
     * <p>
     * A tile's badge is filled with its tone, and a selected tile is filled with `primaryColor.A50` -
     * so `primary`, whose background is that same A50, makes a badge that is nearly white on an
     * unselected tile and invisible on a selected one. This is the shade for a tile that means
     * "attention, nothing wrong": the icon on it measures 3.54:1, past the 3:1 a graphic needs.
     */
    info: {
        background: theme.palette.primaryColor[100] as string,
        border: alpha(theme.palette.primary.dark, 0.25),
        color: theme.palette.primary.dark,
    },
    success: {
        background: theme.palette.success.light,
        border: alpha(theme.palette.success.dark, 0.25),
        color: theme.palette.success.dark,
    },
    brand: {
        background: theme.palette.brand.light,
        border: alpha(theme.palette.brand.dark, 0.25),
        color: theme.palette.brand.dark,
    },
    warning: {
        background: theme.palette.warning.light,
        border: alpha(theme.palette.warning.dark, 0.25),
        color: theme.palette.warning.dark,
    },
    secondary: {
        background: alpha(theme.palette.secondary.main, 0.12),
        border: alpha(theme.palette.secondary.main, 0.28),
        color: theme.palette.secondary.main,
    },
    danger: {
        background: alpha(theme.palette.error.main, 0.12),
        border: alpha(theme.palette.error.main, 0.28),
        color: theme.palette.error.dark,
    },
    neutral: {
        background: theme.palette.neutral[50],
        border: theme.palette.neutral[200],
        color: theme.palette.neutral[600],
    },
};

export const badgeSx = (tone: StatsStripTone): SxProps<Theme> => ({
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    background: tones[tone].background,
    border: `1px solid ${tones[tone].border}`,
    color: tones[tone].color,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': {fontSize: '19px'},
});

export const pillsSx: SxProps<Theme> = {
    ml: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    flexWrap: 'wrap',
};

export const pillSx = (tone: StatsStripTone, clickable: boolean, active: boolean): SxProps<Theme> => ({
    display: 'flex',
    alignItems: 'center',
    gap: 0.9,
    px: 1.5,
    py: 0.75,
    borderRadius: '8px',
    border: `1px solid ${active ? theme.palette.primary.main : tones[tone].border}`,
    background: active ? theme.palette.primaryColor.A50 : tones[tone].background,
    cursor: clickable ? 'pointer' : 'default',
    transition: 'background 150ms ease, border-color 150ms ease',
    '&:hover': {
        borderColor: clickable ? theme.palette.primary.main : undefined,
    },
});

export const pillDotSx = (tone: StatsStripTone): SxProps<Theme> => ({
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    flexShrink: 0,
    background: tone === 'neutral' ? theme.palette.neutral[400] : tones[tone].color,
});

export const pillLabelSx: SxProps<Theme> = {
    fontSize: '0.6875rem',
    fontWeight: 600,
    color: theme.palette.neutral[600],
};

export const pillValueSx = (tone: StatsStripTone): SxProps<Theme> => ({
    fontSize: '0.9375rem',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '-0.01em',
    color: tone === 'danger' ? tones.danger.color : theme.palette.text.primary,
});

export const tilesSx = (count: number): SxProps<Theme> => ({
    display: 'grid',
    gridTemplateColumns: {
        xs: '1fr',
        sm: 'repeat(2, 1fr)',
        md: `repeat(${Math.min(count, 3)}, 1fr)`,
        lg: `repeat(${count}, 1fr)`,
    },
    borderTop: `1px solid ${theme.palette.neutral[100]}`,
    /**
     * Below md the tiles wrap instead of stacking. Stacked one per row, five counts took a whole
     * screen before the list they are counting even began - and the reader has to see the list to
     * know what the numbers are about.
     *
     * They wrap rather than scroll, which is what this was: a rail with its scrollbar deliberately
     * hidden. The tiles genuinely overflowed - the fifth connector status sat half off the screen -
     * and nothing said so or could move them: no scrollbar to grab, no wheel axis on a narrow
     * desktop window, and a swipe only on the one device out of three. A count is short and there
     * are never many, so two or three to a line puts every one of them on screen with no gesture at
     * all. `auto-fit` with a `1fr` maximum also means the tiles fill the width rather than leaving a
     * ragged end, and `minmax` can only shrink - it can never overflow the card again.
     */
    [theme.breakpoints.down('md')]: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(104px, 1fr))',
        alignItems: 'stretch',
        gap: '8px',
        padding: '12px',
    },
});

export const tileSx = (index: number, total: number, clickable: boolean, active: boolean): SxProps<Theme> => ({
    px: 3,
    py: 2.75,
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
    textAlign: 'left',
    alignItems: 'stretch',
    border: 'none',
    borderRadius: 0,
    borderRight: {xs: 'none', lg: index < total - 1 ? `1px solid ${theme.palette.neutral[100]}` : 'none'},
    borderTop: {xs: index > 0 ? `1px solid ${theme.palette.neutral[100]}` : 'none', lg: 'none'},
    background: active ? theme.palette.primaryColor.A50 : theme.palette.common.white,
    cursor: clickable ? 'pointer' : 'default',
    transition: 'background 150ms ease',
    '&:hover': {
        background: clickable ? theme.palette.primaryColor.A50 : undefined,
    },
    // A card of its own in the wrapped row: it takes the share of the line its track gives it, and
    // the one that is on shows it by its edge rather than by a tint a sunlit phone cannot resolve.
    // `min-width: 0` because a grid item refuses by default to be narrower than its own content,
    // which is how one long German label would push its track - and the whole row - off the card.
    [theme.breakpoints.down('md')]: {
        minWidth: 0,
        px: '12px',
        py: '10px',
        gap: '6px',
        border: `1px solid ${active ? theme.palette.primary.main : 'rgba(0, 0, 0, 0.05)'}`,
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    },
});

export const tileHeadSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.1,
    // On a 104px card the label is the whole head; a 32px badge beside it leaves room for about
    // four characters of a German one.
    [theme.breakpoints.down('md')]: {
        '& > .MuiBox-root': {
            display: 'none',
        },
    },
};

export const tileLabelSx: SxProps<Theme> = {
    fontSize: '0.625rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: theme.palette.neutral[500],
    textAlign: 'left',
};

export const tileValueSx = (tone: StatsStripTone, emphasise: boolean): SxProps<Theme> => ({
    fontSize: '1.875rem',
    fontWeight: 600,
    letterSpacing: '-0.025em',
    lineHeight: 1,
    fontVariantNumeric: 'tabular-nums',
    color: emphasise ? tones[tone].color : theme.palette.text.primary,
    [theme.breakpoints.down('md')]: {
        fontSize: '22px',
    },
});

// In the number's place on an action tile: one line of what it does, as tall as the number it replaces.
export const tileDescriptionSx: SxProps<Theme> = {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.35,
    minHeight: '1.875rem',
    color: theme.palette.text.primary,
    textAlign: 'left',
};

export const tileActionSx: SxProps<Theme> = {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: theme.palette.primary.dark,
    textAlign: 'left',
};
